import { AlertTriangle, ChevronDown, ChevronRight, Clock, Eye, EyeOff, Loader2, Lock, RefreshCw, Shield, ShieldCheck, ShieldX, User } from 'lucide-react';
import { useState } from 'react';

import { formatRelativeTime } from '../lib/format-relative-time';
import { Button } from './Button';
import { Input } from './Input';
import { SecurityFlowDiagram } from './SecurityFlowDiagram';

export interface DataAccessItem {
  system: string;
  data: string;
  accessLevel: 'read' | 'read-write';
  expiry: string;
}

export interface PlanApprovalCardProps {
  title: string;
  steps: string[];
  dataAccess: DataAccessItem[];
  mfaRequired?: boolean;
  mfaError?: string;
  status?: 'pending' | 'approved' | 'denied' | 'loading' | 'error';
  approvedBy?: string | null;
  approvedAt?: string | null;
  denialReason?: string | null;
  onApprove?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  onMfaSubmit?: (code: string) => void;
  className?: string;
}

export function PlanApprovalCard({ title, steps, dataAccess, mfaRequired = false, mfaError, status = 'pending', approvedBy, approvedAt, denialReason, onApprove, onCancel, onRetry, onMfaSubmit, className = '' }: PlanApprovalCardProps) {
  const isResolved = status === 'approved' || status === 'denied';
  const [expanded, setExpanded] = useState(!isResolved);
  const [mfaCode, setMfaCode] = useState('');
  const [securityExpanded, setSecurityExpanded] = useState(false);

  const toggle = () => {
    setExpanded((prev) => !prev);
  };

  if (status === 'loading') {
    return (
      <div
        role="region"
        aria-label="Plan approval"
        className={`relative overflow-hidden rounded-sd-lg border border-sd-border/30 shadow-sd-sm w-full ${className}`.trim()}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-sd-border/50" />
        <div className="pl-sd-4 pr-sd-3 py-sd-3 bg-sd-surface-elevated/30">
          <div aria-live="polite" className="flex items-center gap-sd-2">
            <Loader2 size={18} className="text-sd-text-muted shrink-0 animate-spin" aria-hidden="true" />
            <span className="text-sd-base text-sd-text-muted font-medium">
              Retrieving plan status...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        role="region"
        aria-label="Plan approval"
        className={`relative overflow-hidden rounded-sd-lg border border-sd-border/30 shadow-sd-sm w-full ${className}`.trim()}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-sd-text-muted" />
        <div className="pl-sd-4 pr-sd-3 py-sd-3 bg-sd-surface-elevated/30">
          <div className="flex items-center justify-between">
            <div aria-live="polite" className="flex items-center gap-sd-2">
              <AlertTriangle size={18} className="text-sd-text-muted shrink-0" aria-hidden="true" />
              <span className="text-sd-base text-sd-text-muted font-medium">
                Unable to load plan status
              </span>
            </div>
            {onRetry && (
              <Button
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); onRetry(); }}
                className="flex items-center gap-sd-1 text-sd-sm text-sd-primary"
              >
                <RefreshCw size={14} aria-hidden="true" />
                Reload
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Styling per status
  const borderColor = status === 'approved' ? 'border-sd-success/30' : status === 'denied' ? 'border-sd-border/30' : 'border-sd-warning/30';
  const accentColor = status === 'approved' ? 'bg-sd-success' : status === 'denied' ? 'bg-sd-text-muted/50' : 'bg-sd-warning';
  const bgTint = status === 'approved' ? 'bg-sd-success/5' : status === 'denied' ? 'bg-sd-surface-elevated/30' : 'bg-sd-surface';
  const pulseClass = status === 'pending' ? 'sd-gate-pulse shadow-sd-md' : 'shadow-sd-sm';

  return (
    <div
      role="region"
      aria-label="Plan approval"
      className={`relative overflow-hidden rounded-sd-lg border ${borderColor} ${bgTint} ${pulseClass} transition-shadow duration-200 w-full ${className}`.trim()}
    >
      {/* Left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${accentColor}`} />

      {/* Header — always visible */}
      <div className="pl-sd-4 pr-sd-3 pt-sd-3 pb-sd-2">
        {/* Status badge */}
        {status === 'pending' && (
          <div className="flex items-center gap-sd-1 mb-sd-1">
            <Lock size={11} className="text-sd-warning" aria-hidden="true" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-sd-warning">
              Approval Required
            </span>
          </div>
        )}

        {status === 'approved' && (
          <div aria-live="polite" className="flex items-center gap-sd-2 mb-sd-1 sd-fade-in">
            <ShieldCheck size={16} className="text-sd-success shrink-0" aria-hidden="true" />
            <span className="text-sd-sm text-sd-success font-medium">
              Plan approved — agent is working on it
            </span>
          </div>
        )}

        {status === 'denied' && (
          <div aria-live="polite" className="flex items-center gap-sd-2 mb-sd-1 sd-fade-in">
            <ShieldX size={16} className="text-sd-text-muted shrink-0" aria-hidden="true" />
            <span className="text-sd-sm text-sd-text-muted font-medium">
              Plan cancelled
            </span>
          </div>
        )}

        {/* Expandable title row */}
        <div
          role="button"
          aria-expanded={expanded}
          tabIndex={0}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle();
            } else if (e.key === 'Escape' && expanded) {
              setExpanded(false);
            }
          }}
          className="flex items-center justify-between cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 rounded-sd-sm -mx-sd-1 px-sd-1"
        >
          <span className="text-sd-base font-semibold text-sd-text">{title}</span>
          <span className="flex items-center gap-sd-1 text-sd-sm text-sd-text-muted shrink-0 ml-sd-2">
            {expanded
              ? <ChevronDown size={14} aria-hidden="true" />
              : <><ChevronRight size={14} aria-hidden="true" /><span>{isResolved ? 'View details' : 'Review plan'}</span></>
            }
          </span>
        </div>

        {/* Security assurance on approved state (visible when collapsed) */}
        {status === 'approved' && !expanded && (
          <div className="flex items-center gap-sd-1 mt-sd-1 ml-[24px]">
            <Lock size={10} className="text-sd-success/60" aria-hidden="true" />
            <span className="text-[10px] text-sd-success/60">
              Zero-trust protection active — credentials never exposed to AI
            </span>
          </div>
        )}
      </div>

      {/* Expandable content */}
      <div className={`overflow-hidden transition-[max-height,opacity] duration-150 ease-in-out motion-reduce:transition-none ${expanded ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="flex flex-col gap-sd-4 px-sd-4 pb-sd-3">
          {/* Divider between header and content */}
          <div className="border-t border-sd-border/30" />

          {/* Approval/cancellation metadata */}
          {status === 'approved' && (approvedBy || approvedAt) && (
            <div className="flex flex-wrap items-center gap-sd-3 text-sd-xs text-sd-text-secondary">
              {approvedBy && (
                <span className="flex items-center gap-sd-1">
                  <User size={12} aria-hidden="true" />
                  <span>Approved by {approvedBy}</span>
                </span>
              )}
              {approvedAt && (
                <span className="flex items-center gap-sd-1">
                  <Clock size={12} aria-hidden="true" />
                  <span>{formatRelativeTime(approvedAt)}</span>
                </span>
              )}
            </div>
          )}

          {status === 'denied' && (
            <div className="flex flex-col gap-sd-1 text-sd-xs text-sd-text-secondary">
              {denialReason && (
                <span>Reason: {denialReason}</span>
              )}
            </div>
          )}

          <div role="group" aria-label="Plan steps">
            <h3 className="text-sd-sm font-semibold uppercase tracking-wide text-sd-text-secondary mb-sd-2">Plan Steps</h3>
            <div className="flex flex-col gap-sd-2 max-h-[300px] overflow-y-auto">
              {steps.map((step, i) => {
                const trimmed = step.trim();
                if (!trimmed) return null;
                return (
                  <div key={i} className="flex gap-sd-2 text-sd-sm text-sd-text leading-relaxed">
                    <span className="flex items-center justify-center w-[20px] h-[20px] rounded-sd-full bg-sd-surface-elevated text-sd-xs font-medium text-sd-text-secondary shrink-0 mt-[1px]">{i + 1}</span>
                    <span>{trimmed}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {dataAccess.length > 0 && (
            <div role="group" aria-label="Data access" className="border-t border-sd-border/30 pt-sd-3">
              <h3 className="text-sd-sm font-semibold uppercase tracking-wide text-sd-text-secondary mb-sd-2">Data Access</h3>
              <div className="overflow-x-auto rounded-sd-md border border-sd-border/40">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-sd-surface-elevated/60">
                      <th scope="col" className="text-sd-xs font-medium uppercase tracking-wider text-sd-text-secondary py-sd-2 px-sd-3">System</th>
                      <th scope="col" className="text-sd-xs font-medium uppercase tracking-wider text-sd-text-secondary py-sd-2 px-sd-3">Data Accessed</th>
                      <th scope="col" className="text-sd-xs font-medium uppercase tracking-wider text-sd-text-secondary py-sd-2 px-sd-3">Access Level</th>
                      <th scope="col" className="text-sd-xs font-medium uppercase tracking-wider text-sd-text-secondary py-sd-2 px-sd-3">Protection</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataAccess.map((item, i) => (
                      <tr key={i} className="border-t border-sd-border/30 hover:bg-sd-surface-elevated/50 transition-colors duration-100">
                        <td className="text-sd-sm font-medium text-sd-text py-sd-2 px-sd-3">{item.system}</td>
                        <td className="text-sd-sm text-sd-text py-sd-2 px-sd-3">{item.data}</td>
                        <td className="text-sd-sm py-sd-2 px-sd-3">
                          <span className={`inline-flex items-center text-sd-xs font-medium px-sd-2 py-0.5 rounded-sd-full ${item.accessLevel === 'read' ? 'bg-sd-info/10 text-sd-info' : 'bg-sd-warning/10 text-sd-warning'}`}>
                            {item.accessLevel === 'read' ? 'Read-only' : 'Read-Write'}
                          </span>
                        </td>
                        <td className="text-sd-sm py-sd-2 px-sd-3">
                          <span className="inline-flex items-center gap-1 text-sd-xs font-medium text-sd-success">
                            <EyeOff size={11} aria-hidden="true" />
                            <span>Vault-protected</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Credential security summary — subtle but present */}
              <div className="flex items-start gap-sd-2 mt-sd-2 px-sd-1">
                <Shield size={13} className="text-sd-text-muted shrink-0 mt-[2px] sd-shield-breathe" aria-hidden="true" />
                <p className="text-[11px] leading-relaxed text-sd-text-muted">
                  Credentials are encrypted in a vault and never shared with the AI.
                  The agent uses secure aliases — real keys are injected only at the network boundary, scoped to this plan.
                </p>
              </div>
            </div>
          )}

          {/* Security education — expandable "How your data stays secure" */}
          {!isResolved && (
            <div role="group" aria-label="Security details" className="border-t border-sd-border/30 pt-sd-3">
              <button
                type="button"
                onClick={() => setSecurityExpanded((p) => !p)}
                aria-expanded={securityExpanded}
                className="flex items-center justify-between w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 rounded-sd-sm -mx-sd-1 px-sd-1 group"
              >
                <div className="flex items-center gap-sd-2">
                  <Shield size={14} className="text-sd-primary" aria-hidden="true" />
                  <span className="text-sd-sm font-semibold text-sd-text-secondary group-hover:text-sd-text transition-colors duration-100">
                    How your data stays secure
                  </span>
                </div>
                {securityExpanded
                  ? <ChevronDown size={14} className="text-sd-text-muted" aria-hidden="true" />
                  : <ChevronRight size={14} className="text-sd-text-muted" aria-hidden="true" />
                }
              </button>

              <div className={`overflow-hidden transition-[max-height,opacity] duration-200 ease-in-out motion-reduce:transition-none ${securityExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="mt-sd-3 flex flex-col gap-sd-3">
                  {/* Flow diagram */}
                  <SecurityFlowDiagram className="sd-reveal-1" />

                  {/* Security guarantees — business-friendly language */}
                  <div className="grid grid-cols-1 @md:grid-cols-3 gap-sd-2 sd-reveal-2">
                    <SecurityGuarantee
                      icon={<EyeOff size={14} />}
                      title="AI never sees keys"
                      description="Your API credentials stay encrypted. The AI only works with safe, revocable aliases."
                    />
                    <SecurityGuarantee
                      icon={<Lock size={14} />}
                      title="Plan-scoped access"
                      description="Each credential is scoped to this specific plan. It cannot be reused elsewhere."
                    />
                    <SecurityGuarantee
                      icon={<Eye size={14} />}
                      title="Full audit trail"
                      description="Every credential access is logged. You can review exactly what was accessed and when."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {status === 'pending' && mfaRequired && (
            <div role="group" aria-label="Verification" className="border-t border-sd-border/30 pt-sd-3">
              <h3 className="text-sd-sm font-semibold uppercase tracking-wide text-sd-text-secondary mb-sd-2">Verification</h3>
              <div className="flex flex-col gap-sd-2">
                <label htmlFor="mfa-code" className="text-sd-sm text-sd-text-secondary">
                  Enter verification code
                </label>
                <Input
                  id="mfa-code"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setMfaCode(val);
                    if (val.length === 6) {
                      onMfaSubmit?.(val);
                    }
                  }}
                  error={mfaError}
                  className="max-w-[200px]"
                />
              </div>
            </div>
          )}

          {/* Action buttons — only for pending status */}
          {status === 'pending' && (
            <div className="flex flex-col @md:flex-row gap-sd-2 pt-sd-1">
              <Button
                variant="primary"
                onClick={(e) => { e.stopPropagation(); onApprove?.(); }}
                aria-label="Approve plan"
                className="flex-1 min-h-[44px]"
              >
                Approve
              </Button>
              <Button
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); onCancel?.(); }}
                aria-label="Cancel plan"
                className="flex-1 min-h-[44px] text-sd-text-secondary hover:bg-sd-surface-elevated/60"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SecurityGuarantee({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex items-start gap-sd-2 p-sd-2 rounded-sd-md bg-sd-surface-elevated/40">
      <div className="text-sd-primary shrink-0 mt-[1px]" aria-hidden="true">{icon}</div>
      <div>
        <div className="text-sd-xs font-semibold text-sd-text mb-0.5">{title}</div>
        <div className="text-[10.5px] leading-relaxed text-sd-text-muted">{description}</div>
      </div>
    </div>
  );
}
